import type { ShallowRef } from "vue"

import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { StaticSearch } from "~/classes/search/StaticSearch"

import type { PropertyId } from "../entity"

import { withReactive } from "../entity/ReactiveEntity"
import { sendSearchPropertiesRequests } from "../handlers/search/properties"
import { BaseSearch } from "./BaseSearch"

export interface ReactiveSearchOptions {
  /**
   * If true, the search properties will be requested immediately after the component is mounted
   * @default false
   */
  immediate?: boolean
  /**
   * If true, the search properties will not be requested from the server, but updates will
   * still be integrated via events
   * @default false
   */
  noRequest?: boolean

  /**
   * If true, the search will not be registered for lifecycle events (mount/unmount)
   * @default false
   */
  noLifecycle?: boolean
}

export class ReactiveSearch extends withReactive<
  SearchProperty,
  Search,
  StaticSearch,
  typeof BaseSearch
>(BaseSearch) {
  _inner: ShallowRef<StaticSearch> | WritableComputedRef<StaticSearch>
  _innerId: string

  isSetup = false
  isDisposed = false
  options?: ReactiveSearchOptions
  fetchSelection: InterfaceSelection
  location: SelectionLocation

  /**
   * The selections are only used in "immediate" mode. We currently don't automatically
   * check whether the search has the requested selection in non-immediate mode.
   */
  constructor({ search, selection, location, options }: {
    search: Ref<StaticSearch>
    selection?: InterfaceSelection
    pathSelection?: InterfaceSelection
    location: SelectionLocation
    options?: ReactiveSearchOptions
  }) {
    super()
    this._inner = search
    this._innerId = search.value.id
    this.fetchSelection = selection ?? InterfaceSelection.minimumSearchInterfaceViewSelection()
    this.location = location
    this.options = options

    // in immediate mode we have to register the callbacks immediately
    if (this.options?.immediate)
      this.use()

    if (!this.options?.noLifecycle) {
      onMounted(this.use)
      onUnmounted(this.dispose)
    }
  }

  intoStatic = () => new StaticSearch(this.inner())
  inner = () => this.asStatic().inner()

  override addProperty(property: FileProperty): void {
    const blank = StaticSearch.blank(this.inner().id)
    blank.addProperty(property)
    this.mergeWithNewer(blank)
  }

  refresh = () => {
    const { isOffline } = useCache()

    if (this.options?.noRequest)
      return
    if (!this._inner.value)
      throw new Error("Cannot request file properties when file is undefined")

    sendSearchPropertiesRequests([{
      requestId: generateWorkerRequestId(),
      searchId: this._inner.value.id,
      offline: isOffline(),
      selection: this.fetchSelection.inner(),
    }])
  }

  onSearchPropertiesChanged = (search: SearchEvent) => {
    if (search.id !== this._inner.value?.id)
      return console.warn("Reactive search received properties for a different search", search, this._inner.value)
    if (!this._inner.value)
      this._inner.value = new StaticSearch(search)
    this._inner.value.mergeWithNewer(new StaticSearch(search))
  }

  updateProperty = <SearchUpdateRequestDatagram>(
    _propertyId: PropertyId,
    _newValue: EntityPropertyValue,
    _deleted?: boolean,
    _unversioned?: boolean,
  ): {
    updated: StaticSearch
    request: SearchUpdateRequestDatagram
  } => {
    throw new Error("Search properties are not editable yet")
  }

  use = () => {
    if (this.isSetup)
      return this

    this.isSetup = true

    const { addEntityEventListener } = useEvents()

    if (this._inner.value) {
      const searchId = this._inner.value.id
      addEntityEventListener("searchProperties", searchId, this.onSearchPropertiesChanged)
    }

    if (this.options?.immediate)
      this.refresh()

    return this
  }

  dispose = () => {
    const { removeEntityEventListener } = useEvents()

    if (this.isDisposed)
      return console.error("Reactive search was already disposed")
    this.isDisposed = true

    removeEntityEventListener("searchProperties", this._innerId, this.onSearchPropertiesChanged)
  }
}
